// =============================================================================
// ui/state/patterns.tsx — the pattern catalogue.
// -----------------------------------------------------------------------------
// Loaded once at the shell. Three surfaces have been blocked on it: the
// annotation editor's picker, Setup Quality, and the global filter's Pattern facet.
//
// ⚠ NULL until a read succeeds. An empty catalogue is a legitimate state — a new
// user has no patterns — but a FAILED read is not, and `io/sync/patterns.ts`
// throws so this provider never has to guess which it holds.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadPatterns, type PatternSummary } from '../../io/sync/patterns';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface PatternsState {
  phase: 'loading' | 'ready' | 'failed';
  patterns: readonly PatternSummary[] | null;
  error: string | null;
  reload: () => void;
}

const Ctx = createContext<PatternsState | null>(null);

export function usePatterns(): PatternsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePatterns() outside <PatternsProvider>');
  return v;
}

export function PatternsProvider({ sb, children }: { sb: SupabaseClient; children: ReactNode }) {
  const [phase, setPhase] = useState<PatternsState['phase']>('loading');
  const [patterns, setPatterns] = useState<readonly PatternSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        const p = await loadPatterns(sb);
        if (cancelled) return;
        setPatterns(p);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('patterns', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const value = useMemo<PatternsState>(
    () => ({ phase, patterns, error, reload }), [phase, patterns, error, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
